'use client'

import { useRef, useMemo, useEffect, useLayoutEffect, useState } from 'react'
import { Stage, Layer, Rect, Circle, Group, Image as KonvaImage, Text } from 'react-konva'

// ── Canvas ──────────────────────────────────────────
const W = 750
const H = 410
const MOBILE_TARGET_H = 350   // altezza target card su mobile (px)

// ── Grid geometry ────────────────────────────────────
const SPOT_R = 4
const HGAP   = 16
const VGAP   = 16

// Block A — left (larger)
const A_ROWS = 4
const A_COLS = 23
const A_X    = 165   // first column x
const A_Y    = 234   // bottom row aligns with mid gazebo section

// Block B — right (smaller, same top row, fewer rows)
const B_ROWS = 4
const B_COLS = 6
const B_X    = 575   // first column x
const B_Y    = 210   // first row y

// ── Hidden spots (not rendered) ───────────────────────
const SKIP_SPOTS = new Set(['A-0-0', 'A-1-0', 'A-3-20', 'A-3-21', 'A-3-22', 'B-3-0', 'B-3-1', 'B-3-2', 'B-3-3'])

const COLORS = {
  free:            '#4ade80',
  organic_paid:    '#3b82f6',
  manual_paid:     '#ef4444',
  unpaid:          '#fbbf24',
  selected:        '#8b5cf6',
  arrived:         '#d946ef',
  pending_payment: '#f97316',
}
const STRKS = {
  free:            '#16a34a',
  organic_paid:    '#1d4ed8',
  manual_paid:     '#b91c1c',
  unpaid:          '#d97706',
  selected:        '#6d28d9',
  arrived:         '#a21caf',
  pending_payment: '#ea580c',
}

// ── Left gazebos (OVEST) — 2×2, positioned lower-left ─
const GZ_L = 17
const GZ_LGX = 11  // gap orizzontale
const GZ_LGY = 10  // gap verticale
const GZ_LX  = 32
const GZ_LY  = 242
const LEFT_GAZEBO_BASE = [
  { id: 'ov-1', x: GZ_LX,              y: GZ_LY               },
  { id: 'ov-2', x: GZ_LX+GZ_L+GZ_LGX, y: GZ_LY               },
  { id: 'ov-3', x: GZ_LX,              y: GZ_LY+GZ_L+GZ_LGY   },
  { id: 'ov-4', x: GZ_LX+GZ_L+GZ_LGX, y: GZ_LY+GZ_L+GZ_LGY   },
]
const LEFT_GAZEBO_DEFAULT = { 'ov-1': 'free', 'ov-2': 'free', 'ov-3': 'free', 'ov-4': 'free' }

// ── Right gazebos (EST) — 2×2, upper-right ────────────
const GZ_R  = 15
const GZ_RG = 10
const GZ_RX = B_X + B_COLS * HGAP + 14   // right of block B
const GZ_RY = GZ_LY + 2
const RIGHT_GAZEBO_BASE = [
  { id: 'est-1', x: GZ_RX,             y: GZ_RY             },
  { id: 'est-3', x: GZ_RX,             y: GZ_RY+GZ_R+GZ_RG  },
  { id: 'est-4', x: GZ_RX+GZ_R+GZ_RG,  y: GZ_RY+GZ_R+GZ_RG  },
]
const RIGHT_GAZEBO_DEFAULT = { 'est-1': 'free', 'est-3': 'free', 'est-4': 'free' }

// ── Availability parsing ───────────────────────────────
// section=3: gazebi_est  → row/col → est-*
// section=4: gazebi_ovest → row/col → ov-*
const EST_MAP   = { '1-1': 'est-1', '2-1': 'est-3', '2-2': 'est-4' }
const OVEST_MAP = { '1-1': 'ov-1',  '1-2': 'ov-2',  '2-1': 'ov-3', '2-2': 'ov-4' }

function bookingStatus(item) {
  if (item.isAvailable) return 'free'
  if (item.booking?.umbrellaBookings?.find(u => u.umbrellaId === item.id)?.arrivedAt) return 'arrived'
  if (item.isPaid && item.bookingType === 1) return 'manual_paid'
  if (item.isPaid && item.bookingType === 0) return 'organic_paid'
  if (!item.booking) return 'pending_payment'
  return 'unpaid'
}

function parseAvailability(availability) {
  if (!availability?.data) return null
  const items = availability.data
  if (!Array.isArray(items) || !items.length) return null

  const statusA = new Map()
  const statusB = new Map()
  const gazeboStatus = {}

  for (const item of items) {
    const { section, row, seat } = item
    const col = parseInt(seat, 10)
    const status = bookingStatus(item)

    if (section === 1) {
      statusA.set(`A-${row - 1}-${col - 1}`, status)
    } else if (section === 2) {
      statusB.set(`B-${row - 1}-${col - 1}`, status)
    } else if (section === 3) {
      const id = EST_MAP[`${row}-${col}`]
      if (id) gazeboStatus[id] = status
    } else if (section === 4) {
      const id = OVEST_MAP[`${row}-${col}`]
      if (id) gazeboStatus[id] = status
    }
  }

  return { statusA, statusB, gazeboStatus }
}

// ── Building zone ─────────────────────────────────────

function buildSpotGrid({ prefix, rows, cols, x0, y0, statusMap, selected, blockedSpots, adminColors }) {
  const spots = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const key = `${prefix}-${r}-${c}`
      if (SKIP_SPOTS.has(key)) continue
      const apiStatus = statusMap?.get(key) ?? 'free'
      const isBlocked = blockedSpots?.has(key) ?? false
      const spotStatus = selected.has(key) ? 'selected' : isBlocked ? 'unpaid' : apiStatus
      spots.push({
        key, x: x0 + c * HGAP, y: y0 + r * VGAP,
        status: spotStatus,
        label: '',
        shortLabel: '',
        clickable: (apiStatus === 'free' && !isBlocked) || (adminColors && (apiStatus === 'unpaid' || apiStatus === 'manual_paid' || apiStatus === 'organic_paid' || apiStatus === 'arrived')),
      })
    }
  }
  return spots
}

// Assegna a ogni spot un numero progressivo per fila (senza salti),
// indipendente dagli spot saltati. Numerazione decrescente da sinistra a destra.
function renumberByRow(spots) {
  const byRow = {}
  for (const spot of spots) {
    const r = spot.key.split('-')[1]
    if (!byRow[r]) byRow[r] = []
    byRow[r].push(spot)
  }
  const result = []
  for (const r of Object.keys(byRow).sort((a, b) => +a - +b)) {
    const rowSpots = byRow[r].slice().sort((a, b) => a.x - b.x)
    const rowLetter = String.fromCharCode(65 + parseInt(r))
    const total = rowSpots.length
    rowSpots.forEach((spot, idx) => {
      const num = total - idx
      result.push({
        ...spot,
        label: `Fila ${rowLetter} N°${num}`,
        shortLabel: `${rowLetter}${num}`,
      })
    })
  }
  return result
}

// ─────────────────────────────────────────────────────
export default function BeachMap({ selected, onToggle, scale, onZoom, availability, blockedSpots, desktopExpand, adminColors = false }) {
  const stageRef     = useRef(null)
  const containerRef = useRef(null)
  const lastDist            = useRef(null)
  const localScaleRef       = useRef(scale)
  const prevScaleRef        = useRef(scale)
  const gestureStartScale   = useRef(null)
  const isPinchingRef       = useRef(false)
  const wasPinchingRef      = useRef(false)
  const [fitScale, setFitScale] = useState(() => {
    if (window.innerWidth < 768) {
      const approxFit = MOBILE_TARGET_H / H
      return MOBILE_TARGET_H / (H + 30 / approxFit)
    }
    return Math.min(desktopExpand && window.innerWidth >= 768 ? 1.3 : 1, (window.innerWidth - 52) / W)
  })
  const [containerW, setContainerW] = useState(() => window.innerWidth - 52)
  const fitScaleRef  = useRef(fitScale)
  const containerWRef = useRef(containerW)
  useEffect(() => { fitScaleRef.current = fitScale }, [fitScale])
  useEffect(() => { containerWRef.current = containerW }, [containerW])
  useEffect(() => { localScaleRef.current = scale }, [scale])

  // Centra il canvas al mount
  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const isMobile = window.innerWidth < 768
    const fs = fitScaleRef.current || 1
    if (scale > 1.0) {
      const s = scale * fs
      const cx = stage.width() / 2
      const cy = stage.height() / 2
      stage.position({
        x: Math.min(0, Math.max(-W * fs * (scale - 1), cx - (W / 2) * s)),
        y: Math.min(0, Math.max(-H * fs * (scale - 1), cy - (H / 2) * s)),
      })
      stage.draggable(true)
    } else if (isMobile) {
      const cw = containerRef.current ? containerRef.current.offsetWidth : window.innerWidth - 52
      const xOverflow = Math.max(0, W * fs - cw)
      if (xOverflow > 0) {
        stage.position({ x: -xOverflow / 2, y: 0 })
        stage.draggable(true)
      }
    }
    prevScaleRef.current = scale
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const displayStatus = (status) =>
    adminColors || status === 'free' || status === 'selected' ? status : 'unpaid'

  const selFill    = adminColors ? '#8b5cf6' : '#3b82f6'
  const selStrk    = adminColors ? '#6d28d9' : '#1d4ed8'
  const selBoxFill = adminColors ? '#ede9fe' : '#dbeafe'

  const setCursor = (c) => { if (stageRef.current) stageRef.current.container().style.cursor = c }

  useLayoutEffect(() => {
    function updateFit() {
      const isMobile = window.innerWidth < 768
      const w = containerRef.current ? containerRef.current.offsetWidth : window.innerWidth - 52
      if (isMobile) {
        const approxFit = MOBILE_TARGET_H / H
        setFitScale(MOBILE_TARGET_H / (H + 30 / approxFit))
      } else {
        setFitScale(Math.min(desktopExpand && w >= 768 ? 1.3 : 1, w / W))
      }
      setContainerW(w)
    }
    updateFit()
    window.addEventListener('resize', updateFit)
    return () => window.removeEventListener('resize', updateFit)
  }, [])

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return

    if (wasPinchingRef.current) {
      wasPinchingRef.current = false
      prevScaleRef.current = scale
      return
    }

    const fs = fitScaleRef.current || 1
    const prevRawScale = prevScaleRef.current
    const nextRawScale = scale

    const isMobile = window.innerWidth < 768
    const cw = containerRef.current ? containerRef.current.offsetWidth : containerW
    const xOverflow = isMobile ? Math.max(0, W * fs - cw) : 0

    if (nextRawScale <= 1.0) {
      if (xOverflow > 0) {
        const currentPos = stage.position()
        stage.position({
          x: Math.min(0, Math.max(-xOverflow, currentPos.x)),
          y: 0,
        })
        stage.draggable(true)
      } else {
        stage.position({ x: 0, y: 0 })
        stage.draggable(false)
      }
      prevScaleRef.current = nextRawScale
      return
    }

    if (Math.abs(nextRawScale - prevRawScale) < 0.0001) {
      stage.draggable(true)
      prevScaleRef.current = nextRawScale
      return
    }

    const centerX = stage.width() / 2
    const centerY = stage.height() / 2
    const prevScale = prevRawScale * fs
    const nextScale = nextRawScale * fs
    const pos = stage.position()

    const worldX = (centerX - pos.x) / prevScale
    const worldY = (centerY - pos.y) / prevScale
    const minX = -(W * fs * (nextRawScale - 1) + xOverflow)
    const minY = -H * fs * (nextRawScale - 1)

    stage.position({
      x: Math.min(0, Math.max(minX, centerX - worldX * nextScale)),
      y: Math.min(0, Math.max(minY, centerY - worldY * nextScale)),
    })
    stage.draggable(true)
    prevScaleRef.current = nextRawScale
  }, [scale, containerW])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const getDistance = (t1, t2) =>
      Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        const stage = stageRef.current
        const fs = fitScaleRef.current || 1
        if (stage) {
          stage.stopDrag()
          stage.draggable(false)
          localScaleRef.current = stage.scaleX() / fs
        }
        isPinchingRef.current = true
        lastDist.current = getDistance(e.touches[0], e.touches[1])
        gestureStartScale.current = localScaleRef.current
      }
    }

    const onTouchMove = (e) => {
      if (e.touches.length !== 2 || lastDist.current == null || !stageRef.current) return
      e.preventDefault()

      const dist = getDistance(e.touches[0], e.touches[1])
      const ratio = dist / lastDist.current
      lastDist.current = dist

      const stage = stageRef.current
      const fs = fitScaleRef.current
      const oldRawScale = stage.scaleX() / fs
      const newRawScale = Math.min(5.0, Math.max(1.0, oldRawScale * ratio))
      const oldScale = oldRawScale * fs
      const newScale = newRawScale * fs

      const rect = el.getBoundingClientRect()
      const focalX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left
      const focalY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top

      const pos = stage.position()
      const canvasX = (focalX - pos.x) / oldScale
      const canvasY = (focalY - pos.y) / oldScale

      stage.scale({ x: newScale, y: newScale })
      stage.position({
        x: Math.min(0, Math.max(-W * fs * (newRawScale - 1), focalX - canvasX * newScale)),
        y: Math.min(0, Math.max(-H * fs * (newRawScale - 1), focalY - canvasY * newScale)),
      })

      localScaleRef.current = newRawScale
    }

    const onTouchEnd = (e) => {
      if (e.touches.length >= 2) return

      if (gestureStartScale.current != null) {
        const delta = localScaleRef.current - gestureStartScale.current
        if (Math.abs(delta) > 0.001) {
          wasPinchingRef.current = true
          prevScaleRef.current = localScaleRef.current
          onZoom(delta)
        }
        gestureStartScale.current = null
      }

      if (stageRef.current) {
        const isMobileTouchEnd = window.innerWidth < 768
        const xOverflowTouchEnd = isMobileTouchEnd ? Math.max(0, W * fitScaleRef.current - containerWRef.current) : 0
        stageRef.current.draggable(localScaleRef.current > 1.0 || xOverflowTouchEnd > 0)
      }

      isPinchingRef.current = false
      lastDist.current = null
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    el.addEventListener('touchcancel', onTouchEnd)

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [onZoom])

  const mobileXOverflow = window.innerWidth < 768 ? Math.max(0, W * fitScale - containerW) : 0

  const dragBoundFunc = (pos) => ({
    x: Math.min(0, Math.max(-(W * fitScale * (scale - 1) + mobileXOverflow), pos.x)),
    y: Math.min(0, Math.max(-H * fitScale * (scale - 1), pos.y)),
  })

  const parsed = useMemo(() => parseAvailability(availability), [availability])
  const statusA      = parsed?.statusA      ?? new Map()
  const statusB      = parsed?.statusB      ?? new Map()
  const gazeboStatus = parsed?.gazeboStatus ?? {}

  const leftGazebos  = LEFT_GAZEBO_BASE.map(g  => ({ ...g, status: gazeboStatus[g.id]  ?? LEFT_GAZEBO_DEFAULT[g.id]  ?? 'free' }))
  const rightGazebos = RIGHT_GAZEBO_BASE.map(g => ({ ...g, status: gazeboStatus[g.id]  ?? RIGHT_GAZEBO_DEFAULT[g.id] ?? 'free' }))

  const spotsA = useMemo(
    () => buildSpotGrid({ prefix: 'A', rows: A_ROWS, cols: A_COLS, x0: A_X, y0: A_Y, statusMap: statusA, selected, blockedSpots, adminColors }),
    [selected, statusA, blockedSpots, adminColors]
  )
  const spotsB = useMemo(
    () => buildSpotGrid({ prefix: 'B', rows: B_ROWS, cols: B_COLS, x0: B_X, y0: B_Y, statusMap: statusB, selected, blockedSpots, adminColors }),
    [selected, statusB, blockedSpots, adminColors]
  )
  const allSpots = useMemo(
    () => renumberByRow([...spotsA, ...spotsB]),
    [spotsA, spotsB]
  )

  const renderSpot = (u) => (
    <Group
      key={u.key}
      onClick={() => u.clickable && onToggle(u.key, u.label)}
      onTap={() => u.clickable && onToggle(u.key, u.label)}
      onMouseEnter={() => setCursor(u.clickable ? 'pointer' : 'not-allowed')}
      onMouseLeave={() => setCursor('default')}
    >
      <Circle
        x={u.x} y={u.y} radius={SPOT_R}
        fill={u.status === 'selected' ? selFill : COLORS[displayStatus(u.status)]}
        stroke={u.status === 'selected' ? selStrk : STRKS[displayStatus(u.status)]}
        strokeWidth={u.status === 'selected' ? 2 : 0.8}
        shadowColor={u.status === 'selected' ? selStrk : undefined}
        shadowBlur={u.status === 'selected' ? 8 : 0}
        shadowOpacity={0.5}
      />
      <Rect
        x={u.x - 5.5} y={u.y + SPOT_R + 1.5}
        width={11} height={5}
        fill="white" cornerRadius={2.5}
        listening={false}
      />
      <Text
        x={u.x - 5.5} y={u.y + SPOT_R + 2.2}
        width={11} align="center"
        text={u.shortLabel}
        fontSize={4} fill="#222"
        listening={false}
      />
    </Group>
  )

  const BOX_FILL = {
    free:            '#e8f5ec',
    organic_paid:    '#dbeafe',
    manual_paid:     '#fee2e2',
    unpaid:          '#fef3e2',
    selected:        selBoxFill,
    pending_payment: '#ffedd5',
  }
  const BOX_STROKE = {
    free:            '#22c55e',
    organic_paid:    '#1d4ed8',
    manual_paid:     '#b91c1c',
    unpaid:          '#f59e0b',
    selected:        selStrk,
    pending_payment: '#ea580c',
  }

  const renderGazebo = (g, size, label, shortLabel) => {
    const isSel = selected.has(g.id)
    const clickable = g.status === 'free' || isSel || (adminColors && (g.status === 'unpaid' || g.status === 'manual_paid' || g.status === 'organic_paid'))
    const statusKey = isSel ? 'selected' : displayStatus(g.status)
    const fillBox   = BOX_FILL[statusKey]   ?? BOX_FILL.unpaid
    const strokeBox = BOX_STROKE[statusKey] ?? BOX_STROKE.unpaid
    const fillDot   = statusKey === 'selected' ? selFill : (COLORS[statusKey] ?? COLORS.unpaid)
    const strokeDot = statusKey === 'selected' ? selStrk : (STRKS[statusKey]  ?? STRKS.unpaid)
    const cx = g.x + size / 2
    return (
      <Group key={g.id}
        onClick={() => clickable && onToggle(g.id, label)}
        onTap={() => clickable && onToggle(g.id, label)}
        onMouseEnter={() => setCursor(clickable ? 'pointer' : 'not-allowed')}
        onMouseLeave={() => setCursor('default')}
      >
        <Rect x={g.x} y={g.y} width={size} height={size}
          fill={fillBox} stroke={strokeBox}
          strokeWidth={isSel ? 2.5 : 2} cornerRadius={3}
          shadowColor={isSel ? selStrk : undefined}
          shadowBlur={isSel ? 8 : 0} shadowOpacity={0.5}
        />
        <Circle
          x={cx} y={g.y + size / 2}
          radius={size * 0.28}
          fill={fillDot} stroke={strokeDot} strokeWidth={1}
        />
        <Rect
          x={cx - 8} y={g.y + size + 2}
          width={16} height={6}
          fill="white" cornerRadius={2.5}
          listening={false}
        />
        <Text
          x={cx - 8} y={g.y + size + 2.7}
          width={16} align="center"
          text={shortLabel}
          fontSize={4} fill="#222"
          listening={false}
        />
      </Group>
    )
  }

  const [bgImage, setBgImage] = useState(null)
  useEffect(() => {
    const img = new window.Image()
    img.src = '/img/ok@2x.png'
    img.onload = () => setBgImage(img)
  }, [])

  const extH = H + (fitScale < 1 ? 30 / fitScale : 0)

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div className="zoom-controls">
        <button className="zoom-btn" onClick={() => onZoom(0.15)} disabled={scale >= 5.0}>+</button>
        <button className="zoom-btn" onClick={() => onZoom(-0.15)} disabled={scale <= 1.0}>−</button>
      </div>

      <Stage ref={stageRef} width={W * fitScale} height={extH * fitScale} scaleX={scale * fitScale} scaleY={scale * fitScale}
        draggable={scale > 1.0 || mobileXOverflow > 0} dragBoundFunc={dragBoundFunc}>
        <Layer>

          {/* ── Background image ────────────────────── */}
          {bgImage
            ? <KonvaImage image={bgImage} x={0} y={0} width={W} height={extH} />
            : <Rect x={0} y={0} width={W} height={extH} fill="#e8e0d0" />
          }

          {/* tutto il contenuto shiftato di mobileOffset (15px fissi su mobile) */}
          <Group y={fitScale < 1 ? 15 / fitScale : 0}>

            {/* ── Left gazebos ────────────────────────── */}
            {leftGazebos.map((g, i) => renderGazebo(g, GZ_L, `Gazebo OVEST ${i + 1}`, `O${i + 1}`))}

            {/* ── Right gazebos ───────────────────────── */}
            {rightGazebos.map((g, i) => renderGazebo(g, GZ_R, `Gazebo EST ${i + 1}`, `E${i + 1}`))}

            {/* ── Umbrella spots — Block A (left) ─────── */}
            <Group rotation={-5} x={A_X} y={A_Y} offsetX={A_X} offsetY={A_Y}>
              {allSpots.filter(s => s.key.startsWith('A-')).map(renderSpot)}
            </Group>

            {/* ── Umbrella spots — Block B (right) ────── */}
            <Group rotation={10} x={B_X} y={B_Y} offsetX={B_X} offsetY={B_Y}>
              {allSpots.filter(s => s.key.startsWith('B-')).map(renderSpot)}
            </Group>

          </Group>
        </Layer>
      </Stage>
    </div>
  )
}
